import React from 'react';
import { useApp } from '@/contexts/AppContext';
import SeverityBadge from '@/components/apex/SeverityBadge';
import ActionMenu from '@/components/apex/ActionMenu';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Eye, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import InvestigationBadge from '@/components/apex/InvestigationBadge';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

export default function MissingEvidence() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const items = seed?.missing_evidence ?? [];
  const { active, setActive, openPrev, openNext, index, total } = useListDrawer({ items });
  if (!seed) return null;

  const buildRecord = (m) => {
    const tone = m.severity === 'critical' ? 'critical' : m.severity === 'high' ? 'warning' : 'neutral';
    const overdueRatio = m.last_seen_days / Math.max(1, m.history_months * 30 / 12);
    return {
      idTag: m.id,
      title: m.title,
      severity: m.severity,
      subtitle: 'Heartbeat gap detected · APEX expected continuous control evidence',
      verdict: {
        tone,
        label: `${m.last_seen_days}-day silence`,
        sub: `The expected control event "${m.expected}" has not landed in CloudTrail since ${m.last_seen_days} days ago. Auditors read silence as a control gap unless an exception is documented.`,
      },
      signal: {
        tone,
        lead: 'Why APEX surfaced this:',
        body: 'Continuous evidence is the spine of the audit packet. When the expected operational heartbeat stops without an authorized cause, the underlying control is presumed dark — every day adds to the exception register.',
      },
      meta: [
        { label: 'Expected event', value: m.expected, mono: true, sub: 'Required by the operational baseline' },
        { label: 'Time since last beat', value: `${m.last_seen_days}d`, mono: true, tone: 'critical', sub: `${(overdueRatio * 100).toFixed(0)}% of typical cadence`, delta: 'up' },
        { label: 'Evidence horizon', value: `${m.history_months}mo`, mono: true, sub: 'CloudTrail history retained' },
        { label: 'Absence proof status', value: 'PROVEN', mono: true, tone: 'critical', sub: `No ${m.expected} events found · No disable event in CloudTrail` },
        { label: 'Tamper suspicion', value: m.last_seen_days > 7 ? 'ELEVATED' : 'LOW', mono: true, tone: m.last_seen_days > 7 ? 'critical' : 'neutral', sub: 'Unexplained absence without authorized disable event' },
        { label: 'Linked investigation', value: m.investigation ?? 'Not linked', mono: true, tone: m.investigation ? 'critical' : '', sub: m.investigation ? 'Active CISO inquiry · click to open' : 'No open inquiry', pivot: m.investigation ? { to: `/lens/investigations?id=${m.investigation}`, label: `Open ${m.investigation}` } : undefined },
      ],
      sections: [
        {
          title: 'Heartbeat — last 42 days',
          type: 'chart',
          chart: {
            kind: 'pulse',
            label: 'Daily presence of the expected event',
            sublabel: 'Each beat is a successful evidence emission · the flatline is the silence',
            totalDays: 42,
            gapDays: Math.min(42, m.last_seen_days),
          },
        },
        {
          title: 'Governance impact',
          type: 'kpis',
          kpis: [
            {
              label: 'Evidence gap duration',
              sub: 'Days without required control evidence',
              value: `${m.last_seen_days}d`,
              tone: m.last_seen_days > 7 ? 'critical' : 'warning',
              delta: 'up',
            },
            {
              label: 'Governance debt accrued',
              sub: 'From this evidence gap alone',
              value: `$${(m.last_seen_days * 3200).toLocaleString()}`,
              tone: 'critical',
              delta: 'up',
            },
            {
              label: 'Audit risk',
              sub: 'Auditors read silence as a control gap',
              value: m.last_seen_days > 7 ? 'Material' : 'Elevated',
              tone: m.last_seen_days > 7 ? 'critical' : 'warning',
              delta: 'flat',
            },
          ],
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Cadence health', value: m.last_seen_days > 7 ? 'Dark' : 'Lagging', sub: m.last_seen_days > 7 ? 'No qualifying events for over a week' : 'Behind expected cadence', tone: m.last_seen_days > 7 ? 'critical' : 'warning', delta: 'down' },
            { label: 'Audit weight', value: m.investigation ? 'Material' : 'Routine', sub: m.investigation ? 'Surfaces in active investigation' : 'Standard quarterly evidence', tone: m.investigation ? 'critical' : 'lens', delta: 'flat' },
            { label: 'Recovery effort', value: '15–60 min', sub: 'Restore emitter or file Risk Acceptance', tone: 'lens', delta: 'flat' },
          ],
        },
        {
          title: 'Likely root causes',
          type: 'list',
          items: [
            'IAM permission shifted silently — the service account lost the action it needs to emit.',
            'Pipeline owner moved teams; the cron schedule is no longer firing.',
            'Source service was decommissioned but the matching control was never updated.',
          ],
        },
        {
          title: 'What to do next',
          type: 'list',
          items: [
            'Open the linked investigation and inspect the heartbeat heatmap to find the exact silence boundary.',
            'If the gap is intentional, file a Risk Acceptance and link it to this finding so the audit packet inherits the rationale.',
            'Otherwise, restore the source emitter and re-run the control validator to close the gap automatically.',
          ],
        },
        {
          title: 'Recommended next steps',
          type: 'list',
          items: [
            `Verify ${m.expected} is still configured and enabled in stratum-payments-prod.`,
            'Check CloudTrail for DisableConfigRule or DeleteConfigRule events in the last 30 days.',
            'If no authorized event found, escalate to the security team as a potential tampering indicator.',
            'Document the gap in the audit exception register with this evidence ID.',
            'Re-enable the control and verify the next expected event lands within 24 hours.',
          ],
        },
      ],
      actions: [
        {
          label: m.investigation ? `Open ${m.investigation}` : 'Open Investigations',
          primary: true,
          testId: 'me-primary-action',
          onClick: () => navigate(m.investigation ? `/lens/investigations?id=${m.investigation}` : '/lens/investigations'),
        },
        {
          label: 'Open Remediation',
          testId: 'me-open-rem',
          onClick: () => navigate('/lens/remediation'),
        },
        {
          label: 'Export evidence',
          testId: 'me-export',
          onClick: () => toast.success('Evidence packet queued', { description: m.id }),
        },
        {
          label: 'Simulate restoration',
          testId: 'me-simulate-restore',
          onClick: () => {
            toast.success('Evidence heartbeat restored', {
              description: `${m.expected} event detected in ${m.account ?? 'stratum-payments-prod'}. APEX Lens will confirm continuous coverage after the next 24-hour evaluation window. Governance debt accrual paused.`,
              duration: 6000,
            });
          },
        },
      ],
    };
  };

  return (
    <div className="space-y-6" data-testid="missing-evidence">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence Engine · Missing Evidence Detection</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">When evidence stops, APEX notices</h1>
        <p className="text-sm text-apex-neutral mt-1 max-w-3xl">
          Missing Evidence Detection monitors the expected operational heartbeat of your governance environment. When required evidence stops appearing without authorized cause, APEX Lens surfaces it as a governance failure.
        </p>
      </div>

      <div className="space-y-4">
        {seed.missing_evidence.map((m, i) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            data-testid={`missing-evidence-${m.id}`}
            onClick={() => setActive(m)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(m); } }}
            className={`bg-white rounded-lg border p-4 sm:p-5 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all ${m.investigation === 'INV-2026-042' ? 'border-apex-lens ring-1 ring-apex-lens/20' : 'border-slate-200'}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-3.5 h-3.5 text-apex-neutral" />
                  <span className="text-[10px] font-mono text-apex-neutral">{m.id}</span>
                  <SeverityBadge severity={m.severity} size="sm" />
                </div>
                <h3 className="text-base font-semibold text-apex-ink">
                  {m.title}
                  {m.investigation === 'INV-2026-042' && (
                    <span className="inline-block align-middle ml-1.5">
                      <InvestigationBadge />
                    </span>
                  )}
                </h3>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] font-mono text-apex-neutral">
                  <span>Expected: <span className="text-apex-ink-2">{m.expected}</span></span>
                  <span>Last seen: <span className="text-apex-critical">{m.last_seen_days} days ago</span></span>
                  <span>History: <span className="text-apex-ink-2">{m.history_months} months</span></span>
                </div>
                <div className="mt-2 text-[11px] text-apex-warning">No authorized explanation event found in CloudTrail.</div>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {m.investigation && (
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/lens/investigations?id=${m.investigation}`); }} className="text-xs font-semibold text-apex-lens">
                    View {m.investigation} →
                  </button>
                )}
                <ActionMenu context={m.id} testId={`me-actions-${m.id}`} />
              </div>
            </div>

            <div className="mt-3 border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Historical heartbeat · {m.history_months} months</div>
                <span className="inline-flex items-center gap-1 text-[10px] text-apex-lens font-semibold">
                  Open detail <ArrowRight className="w-3 h-3" />
                </span>
              </div>
              <Heatmap months={m.history_months} gapDays={m.last_seen_days} />
            </div>
          </motion.div>
        ))}
      </div>

      <RecordDrawer
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
        record={active ? buildRecord(active) : null}
        testId="me-drawer"
        onPrev={openPrev}
        onNext={openNext}
        position={index}
        total={total}
      />
    </div>
  );
}

function Heatmap({ months = 14, gapDays = 9 }) {
  const totalDays = months * 30;
  const cells = Array.from({ length: totalDays });
  const gapStart = totalDays - gapDays;
  return (
    <div className="grid grid-cols-[repeat(60,minmax(0,1fr))] gap-[2px]">
      {cells.map((_, i) => {
        const inGap = i >= gapStart;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.001, duration: 0.2 }}
            className={`block aspect-square rounded-[1px] ${inGap ? 'bg-apex-critical' : 'bg-emerald-400/70'}`}
          />
        );
      })}
    </div>
  );
}
